import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Button, Form } from 'react-bootstrap';
import InputGroup from 'react-bootstrap/InputGroup';
import DropdownButton from 'react-bootstrap/DropdownButton';
import Dropdown from 'react-bootstrap/Dropdown';
import { Loading, PageHeader } from '../../components';
import DataTable from '../../components/DataTable/DataTable';
import CancelAlertPopup from '../../components/CancelAlertPopup/CancelAlertPopup';
import Pagination from '../../components/pagination/Pagination.component';
import useComponentState from '../../utils/useComponentState';
import styles from './viewMccCodes.module.scss';
import { Drawer } from 'rsuite';
import { toast } from 'react-toastify';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    getAllLoyaltyPointRatios,
    searchLoyaltyPointRatios,
    updateLoyaltyPointRatio,
    deleteLoyaltyPointRatio,
} from '../../api/loyaltyPointRatio';
import { getAllLoyaltyTiers } from '../../api/tier';
import PermissionGate from '../../components/PermissionGate/PermissionGate';
import { usePermissions } from '../../hooks/usePermissions';
import { MCC_MANAGEMENT_PERMISSIONS } from '../../constants/permissions';

const validateEditForm = (editForm) => {
    const errors = {};

    if (!editForm.ratioType || editForm.ratioType.trim().length === 0) {
        errors.ratioType = 'MCC Code is required.';
    }

    const accRatio = parseFloat(editForm.accumulationRatio);
    if (editForm.accumulationRatio === '' || isNaN(accRatio) || accRatio <= 0) {
        errors.accumulationRatio = 'Accumulation ratio must be greater than 0.';
    }

    if (!editForm.loyaltyTierId) {
        errors.loyaltyTierId = 'Loyalty tier is required.';
    }

    return { isValid: Object.keys(errors).length === 0, errors };
};

const ViewMccCodes = (props) => {
    const { auth: { token } } = props.auth;
    const { can } = usePermissions();

    const [state, setState] = useComponentState({
        tableHeading: {
            column: ['MCC Code', 'Accumulation Ratio', 'Tier'],
        },
        ratiosData: [],
        ratiosLoadingError: '',
        loading: false,
        currentPage: 0,
        totalPages: 0,
        tiers: [],

        searchType: 'ratioType',
        searchValue: '',
        label: 'MCC Code',
        searchPlaceholder: 'Search MCC Code...',
        isClearable: false,

        showDrawer: false,
        drawerData: null,

        editMode: false,
        editForm: {
            ratioType: '',
            accumulationRatio: '',
            loyaltyTierId: '',
        },
        fieldErrors: {},
        editSaving: false,

        showDeleteConfirmation: false,
        ratioToDelete: null,
    });

    useEffect(() => {
        fetchData(0, false);
        loadTiers();
        //eslint-disable-next-line
    }, []);

    const fetchData = async (page = 0, isSearch = false, searchType = state.searchType, searchValue = state.searchValue) => {
        setState({ loading: true, ratiosData: [], ratiosLoadingError: '' });
        try {
            let response;
            if (isSearch && searchValue) {
                response = await searchLoyaltyPointRatios(token, {
                    ratioType: searchType === 'ratioType' ? searchValue : undefined,
                    loyaltyTierId: searchType === 'loyaltyTierId' ? searchValue : undefined,
                    page,
                });
            } else {
                response = await getAllLoyaltyPointRatios(token, page);
            }
            setState({
                ratiosData: response.list || [],
                totalPages: response.totalPageCount || 0,
                currentPage: response.pageNo || 0,
            });
        } catch {
            setState({ ratiosLoadingError: 'Oops, something went wrong. Please try again.' });
        }
        setState({ loading: false });
    };

    const handleSearch = () => {
        if (!state.isClearable) {
            if (state.searchValue) {
                setState({ isClearable: true, currentPage: 0 });
                fetchData(0, true, state.searchType, state.searchValue);
            }
        } else {
            setState({ searchValue: '', isClearable: false, currentPage: 0 });
            fetchData(0, false);
        }
    };

    const handlePagination = (page) => {
        setState({ currentPage: page });
        fetchData(page, state.isClearable, state.searchType, state.searchValue);
    };

    const loadTiers = async () => {
        try {
            const tiers = await getAllLoyaltyTiers(token);
            setState({ tiers: tiers || [] });
        } catch {
            // non-critical
        }
    };

    const handleDrawerOpen = (rowData) => {
        if (!rowData) return;
        setState({
            showDrawer: true,
            drawerData: rowData,
            editMode: false,
            editForm: {
                ratioType: rowData.ratioType || '',
                accumulationRatio: rowData.accumulationRatio !== undefined ? String(rowData.accumulationRatio) : '',
                loyaltyTierId: rowData.loyaltyTierId ? String(rowData.loyaltyTierId) : '',
            },
            fieldErrors: {},
        });
    };

    const handleInputChange = (field, value) => {
        setState({
            editForm: { ...state.editForm, [field]: value },
            fieldErrors: { ...state.fieldErrors, [field]: '' },
        });
    };

    const handleSave = async () => {
        if (!can(MCC_MANAGEMENT_PERMISSIONS.VIEW_MCC_CODES.UPDATE)) {
            toast.error('You are not authorized to perform this action.');
            return;
        }
        const { isValid, errors } = validateEditForm(state.editForm);
        if (!isValid) {
            setState({ fieldErrors: errors });
            return;
        }

        const { editForm, drawerData } = state;
        if (!drawerData) return;

        setState({ editSaving: true });

        try {
            const payload = {
                id: drawerData.id,
                ratioType: editForm.ratioType.trim(),
                accumulationRatio: parseFloat(editForm.accumulationRatio),
                loyaltyTierId: parseInt(editForm.loyaltyTierId, 10),
            };

            const updated = await updateLoyaltyPointRatio(token, payload);

            const selectedTier = state.tiers.find((t) => t.id === payload.loyaltyTierId);
            const updatedRecord = {
                ...drawerData,
                ...payload,
                loyaltyTierName: updated?.loyaltyTierName || selectedTier?.name || drawerData.loyaltyTierName,
            };

            setState({
                drawerData: updatedRecord,
                editMode: false,
                editSaving: false,
                ratiosData: state.ratiosData.map((r) => r.id === drawerData.id ? updatedRecord : r),
            });

            toast.success('MCC Code updated successfully.');
        } catch (err) {
            setState({ editSaving: false });
            if (err?.status === 409) {
                const tierId = parseInt(editForm.loyaltyTierId, 10);
                const tierName = state.tiers.find((t) => t.id === tierId)?.name || `ID ${tierId}`;
                toast.error(`Not Updated. MCC code '${editForm.ratioType}' and tier ${tierName} is already exist.`);
            } else {
                toast.error('Failed to update MCC Code. Please try again.');
            }
        }
    };

    const handleCloseDrawer = () => {
        setState({ showDrawer: false, drawerData: null, editMode: false });
    };

    const handleDelete = (rowData) => {
        setState({ showDeleteConfirmation: true, ratioToDelete: rowData });
    };

    const handleConfirmDelete = async () => {
        const record = state.ratioToDelete;
        if (!record) return;

        if (!can(MCC_MANAGEMENT_PERMISSIONS.VIEW_MCC_CODES.DELETE)) {
            toast.error('You are not authorized to perform this action.');
            setState({ showDeleteConfirmation: false, ratioToDelete: null });
            return;
        }

        setState({ loading: true, showDeleteConfirmation: false });

        try {
            await deleteLoyaltyPointRatio(token, record.id);
            setState({
                ratiosData: state.ratiosData.filter((r) => r.id !== record.id),
                showDrawer: false,
                drawerData: null,
                editMode: false,
                loading: false,
                ratioToDelete: null,
            });
            toast.success('MCC Code deleted successfully.');
        } catch {
            setState({ loading: false, ratioToDelete: null });
            toast.error('Failed to delete MCC Code. Please try again.');
        }
    };

    const handleCancelDelete = () => {
        setState({ showDeleteConfirmation: false, ratioToDelete: null });
    };

    const renderSearch = () => (
        <InputGroup className='mb-3'>
            <DropdownButton
                as={InputGroup.Prepend}
                variant='secondary'
                title={state.label}
                id='mcc-search-dropdown'
            >
                <Dropdown.Item
                    onClick={() => setState({ label: 'MCC Code', searchPlaceholder: 'Search MCC Code...', searchType: 'ratioType', searchValue: '', isClearable: false })}
                >
                    MCC Code
                </Dropdown.Item>
                <Dropdown.Item
                    onClick={() => setState({ label: 'Tier', searchPlaceholder: '', searchType: 'loyaltyTierId', searchValue: '', isClearable: false })}
                >
                    Tier
                </Dropdown.Item>
            </DropdownButton>

            {state.searchType === 'ratioType' ? (
                <Form.Control
                    placeholder={state.searchPlaceholder}
                    value={state.searchValue}
                    onChange={(e) => setState({ searchValue: e.target.value, isClearable: false })}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    maxLength={50}
                />
            ) : (
                <Form.Control
                    as='select'
                    value={state.searchValue}
                    onChange={(e) => setState({ searchValue: e.target.value, isClearable: false })}
                >
                    <option value=''>Select a tier</option>
                    {state.tiers.map((tier) => (
                        <option key={tier.id} value={tier.id}>{tier.name}</option>
                    ))}
                </Form.Control>
            )}

            <InputGroup.Append>
                <Button variant='secondary' onClick={handleSearch}>
                    <FontAwesomeIcon icon={state.isClearable ? faTimes : faSearch} />
                </Button>
            </InputGroup.Append>
        </InputGroup>
    );

    const renderDrawer = () => {
        const data = state.drawerData;
        if (!data) return null;

        return (
            <>
                <Drawer.Header>
                    <div className={styles.drawer_header}>
                        <Drawer.Title>
                            {!state.editMode ? 'MCC Code Details' : 'Edit MCC Code'}
                        </Drawer.Title>
                    </div>
                </Drawer.Header>

                <Drawer.Body>
                    <div className={styles.drawer_body}>
                        {!state.editMode ? (
                            <>
                                <div className={styles.detail_row}>
                                    <strong>MCC Code:</strong>
                                    <span>{data.ratioType || 'N/A'}</span>
                                </div>
                                <div className={styles.detail_row}>
                                    <strong>Accumulation Ratio:</strong>
                                    <span>{data.accumulationRatio !== undefined ? data.accumulationRatio : 'N/A'}</span>
                                </div>
                                <div className={styles.detail_row}>
                                    <strong>Loyalty Tier:</strong>
                                    <span>{data.loyaltyTierName || 'N/A'}</span>
                                </div>
                            </>
                        ) : (
                            <div className={styles.edit_form}>
                                <label className={styles.form_label}>
                                    MCC Code *
                                    <input
                                        type='text'
                                        value={state.editForm.ratioType}
                                        onChange={(e) => handleInputChange('ratioType', e.target.value)}
                                        placeholder='e.g. 3193'
                                        autoFocus
                                        className={state.fieldErrors?.ratioType ? styles.input_error : ''}
                                    />
                                    {state.fieldErrors?.ratioType && (
                                        <span className={styles.error_message}>{state.fieldErrors.ratioType}</span>
                                    )}
                                </label>

                                <label className={styles.form_label}>
                                    Accumulation Ratio *
                                    <input
                                        type='number'
                                        min='0.000001'
                                        step='any'
                                        value={state.editForm.accumulationRatio}
                                        onChange={(e) => handleInputChange('accumulationRatio', e.target.value)}
                                        placeholder='e.g. 0.004000'
                                        className={state.fieldErrors?.accumulationRatio ? styles.input_error : ''}
                                    />
                                    {state.fieldErrors?.accumulationRatio && (
                                        <span className={styles.error_message}>{state.fieldErrors.accumulationRatio}</span>
                                    )}
                                </label>

                                <label className={styles.form_label}>
                                    Loyalty Tier *
                                    <select  
                                        value={state.editForm.loyaltyTierId}
                                        onChange={(e) => handleInputChange('loyaltyTierId', e.target.value)}
                                        className={state.fieldErrors?.loyaltyTierId ? styles.input_error : ''}
                                    >
                                        <option value=''>Select a tier</option>
                                        {state.tiers.map((tier) => (
                                            <option key={tier.id} value={tier.id}>{tier.name}</option>
                                        ))}
                                    </select>
                                    {state.fieldErrors?.loyaltyTierId && (
                                        <span className={styles.error_message}>{state.fieldErrors.loyaltyTierId}</span>
                                    )}
                                </label>
                            </div>
                        )}
                    </div>

                    <div className={styles.drawer_actions}>
                        {!state.editMode ? (
                            <>
                                <PermissionGate permission={MCC_MANAGEMENT_PERMISSIONS.VIEW_MCC_CODES.UPDATE}>
                                    <button
                                        type='button'
                                        className='btn btn-primary'
                                        onClick={() => setState({ editMode: true })}
                                    >
                                        Edit
                                    </button>
                                </PermissionGate>
                                <PermissionGate permission={MCC_MANAGEMENT_PERMISSIONS.VIEW_MCC_CODES.DELETE}>
                                    <button
                                        type='button'
                                        className='btn btn-danger'
                                        onClick={() => handleDelete(state.drawerData)}
                                    >
                                        Delete
                                    </button>
                                </PermissionGate>
                            </>
                        ) : (
                            <>
                                <button
                                    type='button'
                                    className='btn btn-secondary'
                                    onClick={() => setState({ editMode: false, fieldErrors: {} })}
                                    disabled={state.editSaving}
                                >
                                    Cancel
                                </button>
                                <button
                                    type='button'
                                    className='btn btn-primary'
                                    onClick={handleSave}
                                    disabled={state.editSaving}
                                >
                                    {state.editSaving ? 'Saving...' : 'Save'}
                                </button>
                            </>
                        )}
                    </div>
                </Drawer.Body>
            </>
        );
    };

    const { tableHeading, ratiosData, ratiosLoadingError, loading, currentPage, totalPages } = state;

    return (
        <div className={styles.root_div}>
            {loading ? (
                <Loading text='Loading' />
            ) : (
                <div>
                    <PageHeader text='MCC Codes' />
                    {ratiosLoadingError ? (
                        <p>{ratiosLoadingError}</p>
                    ) : (
                        <div>
                            <div className={styles.search_area_block}>
                                <div className={styles.search_area}>
                                    {renderSearch()}
                                </div>
                            </div>
                            <DataTable
                                tableData={ratiosData}
                                tableheading={tableHeading}
                                handleOnClick={handleDrawerOpen}
                                tag='mcc codes'
                                fetchError=''
                                searchFilterLoading={false}
                            />
                            {!loading && totalPages > 0 && (
                                <Pagination
                                    pageCount={totalPages}
                                    onChange={handlePagination}
                                    currentPage={currentPage}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}

            <CancelAlertPopup
                show={state.showDeleteConfirmation}
                alertMessage='Are you sure you want to delete this MCC Code? This action cannot be undone.'
                handleYesBtnClick={handleConfirmDelete}
                handleNoBtnClick={handleCancelDelete}
            />

            <Drawer show={state.showDrawer} onHide={handleCloseDrawer} placement='right'>
                {renderDrawer()}
            </Drawer>
        </div>
    );
};

const mapStateToProps = (state) => ({
    auth: state.auth,
});

export default connect(mapStateToProps)(ViewMccCodes);
